/**
 * TrainerGradingQueuePage.jsx — LMS-Growth Sprint
 * -----------------------------------------------
 * Unified queue of submitted position papers + resolutions awaiting chair
 * review.  Roles: chair / faculty_advisor / org_admin / event_manager /
 * super_admin (scope auto-resolved server-side).
 */
import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import {
  getGradingQueue, getGradingMetrics, gradePaper, gradeResolution,
  formatApiError,
} from "../../lib/trainerGradingApi";
import {
  ScrollText, FileCheck2, Filter, RefreshCw, CheckCircle2, XCircle,
  AlertTriangle, ListChecks, MessageSquare, Clock4, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_TABS = [
  { value: "pending",  label: "Pending review" },
  { value: "reviewed", label: "Reviewed" },
  { value: "all",      label: "All submissions" },
];
const TYPE_TABS = [
  { value: "all",         label: "All" },
  { value: "papers",      label: "Papers", icon: ScrollText },
  { value: "resolutions", label: "Resolutions", icon: FileCheck2 },
];
const STATUS_PILL = {
  pending:          { label: "Pending",        cls: "bg-amber-50 text-amber-700 border-amber-200" },
  accepted:         { label: "Accepted",       cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected:         { label: "Rejected",       cls: "bg-rose-50 text-rose-700 border-rose-200" },
  needs_revision:   { label: "Needs revision", cls: "bg-orange-50 text-orange-700 border-orange-200" },
};

export default function TrainerGradingQueuePage() {
  const [status, setStatus] = useState("pending");
  const [itemType, setItemType] = useState("all");
  const [rows, setRows] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);  // row open in side modal

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, m] = await Promise.all([
        getGradingQueue({ status, itemType }),
        getGradingMetrics(),
      ]);
      setRows(q.rows || []);
      setMetrics(m);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [status, itemType]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout title="Grading Queue">
      <div data-testid="trainer-grading-page" className="space-y-6 pb-12">
        <Hero metrics={metrics} onRefresh={load} loading={loading} />
        <Filters
          status={status} setStatus={setStatus}
          itemType={itemType} setItemType={setItemType}
        />
        <QueueTable
          rows={rows} loading={loading}
          onOpen={(r) => setActive(r)}
        />
      </div>

      {active ? (
        <GradeModal
          row={active}
          onClose={() => setActive(null)}
          onGraded={async () => { setActive(null); await load(); }}
        />
      ) : null}
    </DashboardLayout>
  );
}

// =============================================================================
// Hero + metrics
// =============================================================================
function Hero({ metrics, onRefresh, loading }) {
  const pp = metrics?.pending_papers ?? 0;
  const pr = metrics?.pending_resolutions ?? 0;
  const rp = metrics?.reviewed_papers_30d ?? 0;
  const rr = metrics?.reviewed_resolutions_30d ?? 0;
  return (
    <section
      data-testid="grading-hero"
      className="relative overflow-hidden rounded-3xl border border-[#0F4C81] bg-gradient-to-br from-[#0A192F] via-[#0F4C81] to-[#0A192F] p-6 md:p-7 shadow-2xl text-white"
    >
      <div className="absolute -top-32 -right-24 h-72 w-72 rounded-full bg-[#D4AF37]/15 blur-3xl" />
      <div className="relative grid grid-cols-1 gap-6 md:grid-cols-12">
        <div className="md:col-span-7">
          <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-bold flex items-center gap-2">
            <ListChecks size={12} /> Trainer · Grading Queue
          </p>
          <h1 className="mt-1 text-3xl md:text-4xl font-bold leading-tight">
            Review the next delegate submission
          </h1>
          <p className="mt-2 max-w-xl text-sm text-blue-100">
            Papers and resolutions submitted across your events. Accept,
            request revisions, or reject — accepted submissions also
            award Podium Points to the delegate.
          </p>
        </div>
        <div className="md:col-span-5 grid grid-cols-2 gap-3">
          <Kpi testid="grading-kpi-pending-papers"      label="Pending papers"        value={pp} />
          <Kpi testid="grading-kpi-pending-resolutions" label="Pending resolutions"   value={pr} />
          <Kpi testid="grading-kpi-reviewed-papers"     label="Reviewed papers (30d)" value={rp} />
          <Kpi testid="grading-kpi-reviewed-resolutions"label="Reviewed resolutions"  value={rr} />
        </div>
      </div>
      <button
        type="button"
        data-testid="grading-refresh"
        onClick={onRefresh}
        disabled={loading}
        className="absolute top-5 right-5 inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white hover:bg-white/20 disabled:opacity-50"
      >
        {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
        Refresh
      </button>
    </section>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-white"
    >
      <p className="text-[9px] uppercase tracking-[0.22em] text-blue-200/80 font-bold">{label}</p>
      <p className="mt-0.5 text-xl font-bold tabular-nums">{value}</p>
    </div>
  );
}

function Filters({ status, setStatus, itemType, setItemType }) {
  return (
    <section
      data-testid="grading-filters"
      className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm"
    >
      <Filter size={13} className="text-slate-400" />
      <div className="flex items-center rounded-full bg-slate-100 p-0.5">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            data-testid={`grading-status-${t.value}`}
            onClick={() => setStatus(t.value)}
            className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] transition ${
              status === t.value
                ? "bg-[#0A192F] text-[#D4AF37]"
                : "text-slate-600 hover:text-[#0A192F]"
            }`}
          >{t.label}</button>
        ))}
      </div>
      <span className="text-slate-300">·</span>
      <div className="flex items-center rounded-full bg-slate-100 p-0.5">
        {TYPE_TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.value}
              type="button"
              data-testid={`grading-type-${t.value}`}
              onClick={() => setItemType(t.value)}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] transition ${
                itemType === t.value
                  ? "bg-[#0A192F] text-[#D4AF37]"
                  : "text-slate-600 hover:text-[#0A192F]"
              }`}
            >
              {Icon ? <Icon size={11} /> : null} {t.label}
            </button>
          );
        })}
      </div>
    </section>
  );
}

// =============================================================================
// Queue table
// =============================================================================
function QueueTable({ rows, loading, onOpen }) {
  if (loading) {
    return (
      <div
        data-testid="grading-table-loading"
        className="grid h-48 place-items-center rounded-2xl border border-slate-200 bg-white text-sm text-slate-500"
      >Loading the queue…</div>
    );
  }
  if (!rows.length) {
    return (
      <div
        data-testid="grading-empty"
        className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center"
      >
        <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500">
          The queue is clear
        </p>
        <p className="mt-2 text-sm text-slate-600">
          No submissions match the current filter.
        </p>
      </div>
    );
  }
  return (
    <section
      data-testid="grading-table"
      className="overflow-x-auto rounded-2xl border border-slate-200 bg-white p-3 shadow-sm"
    >
      <table className="min-w-full text-[12px]">
        <thead className="bg-slate-50">
          <tr className="text-left text-[9px] uppercase tracking-[0.22em] text-slate-500">
            <th className="px-2 py-2">Type</th>
            <th className="px-2 py-2">Delegate</th>
            <th className="px-2 py-2">Title</th>
            <th className="px-2 py-2">Submitted</th>
            <th className="px-2 py-2">Status</th>
            <th className="px-2 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const pill = STATUS_PILL[r.chair_review_status] || STATUS_PILL.pending;
            return (
              <tr
                key={`${r.kind}-${r.id}`}
                data-testid={`grading-row-${r.id}`}
                className="border-t border-slate-100 hover:bg-slate-50"
              >
                <td className="px-2 py-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#0A192F] px-2 py-0.5 text-[9px] font-bold text-[#D4AF37] uppercase tracking-[0.18em]">
                    {r.kind === "paper" ? <ScrollText size={10} /> : <FileCheck2 size={10} />}
                    {r.kind}
                  </span>
                </td>
                <td className="px-2 py-2">
                  <div className="font-semibold text-[#0A192F]">{r.delegate_name || r.delegate_id}</div>
                  <div className="text-[10px] text-slate-500">
                    {r.country ? `${r.country}` : null}
                    {r.committee_id ? ` · ${r.committee_id}` : null}
                  </div>
                </td>
                <td className="px-2 py-2 text-[#0A192F] max-w-xs truncate" title={r.title}>{r.title || "—"}</td>
                <td className="px-2 py-2 text-slate-500 whitespace-nowrap">
                  <Clock4 size={10} className="inline -mt-0.5 mr-1" />
                  {(r.submitted_at || "").slice(0, 16).replace("T", " ")}
                </td>
                <td className="px-2 py-2">
                  <span
                    data-testid={`grading-status-pill-${r.id}`}
                    className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] ${pill.cls}`}
                  >{pill.label}</span>
                </td>
                <td className="px-2 py-2 text-right">
                  <button
                    type="button"
                    data-testid={`grading-open-${r.id}`}
                    onClick={() => onOpen(r)}
                    className="rounded-lg bg-[#0A192F] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#D4AF37] hover:bg-[#0F4C81]"
                  >Grade</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

// =============================================================================
// Grade modal
// =============================================================================
function GradeModal({ row, onClose, onGraded }) {
  const [status, setStatus] = useState("");
  const [feedback, setFeedback] = useState(row.chair_feedback || "");
  const [saving, setSaving] = useState(false);

  const grade = async () => {
    if (!status) { toast.error("Pick an outcome first."); return; }
    setSaving(true);
    try {
      const body = { status, feedback };
      if (row.kind === "paper") await gradePaper(row.id, body);
      else await gradeResolution(row.id, body);
      toast.success(`Marked as ${status.replace("_", " ")}`);
      await onGraded();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div
      data-testid="grade-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-br from-[#0A192F] to-[#0F4C81] p-5 text-white">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Grade {row.kind}
          </p>
          <h2 className="mt-1 text-lg font-bold">{row.title || "Untitled submission"}</h2>
          <p className="mt-1 text-[11px] text-blue-100">
            {row.delegate_name} {row.country ? `· ${row.country}` : ""}
          </p>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Outcome</p>
            <div className="grid grid-cols-3 gap-2">
              <StatusPick
                testid="grade-pick-accepted"
                active={status === "accepted"}
                onClick={() => setStatus("accepted")}
                icon={CheckCircle2} label="Accept"
                cls="bg-emerald-50 text-emerald-700 border-emerald-300"
              />
              <StatusPick
                testid="grade-pick-needs-revision"
                active={status === "needs_revision"}
                onClick={() => setStatus("needs_revision")}
                icon={AlertTriangle} label="Needs revision"
                cls="bg-orange-50 text-orange-700 border-orange-300"
              />
              <StatusPick
                testid="grade-pick-rejected"
                active={status === "rejected"}
                onClick={() => setStatus("rejected")}
                icon={XCircle} label="Reject"
                cls="bg-rose-50 text-rose-700 border-rose-300"
              />
            </div>
          </div>
          <div>
            <label htmlFor="grade-feedback" className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">
              Feedback for the delegate
            </label>
            <textarea
              id="grade-feedback"
              data-testid="grade-feedback"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              maxLength={4000}
              placeholder="Specific, actionable feedback — what worked, what to improve."
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-[#0F4C81] focus:outline-none"
              rows={6}
            />
            <p className="mt-1 text-[10px] text-slate-400 flex items-center gap-1">
              <MessageSquare size={10} /> Delegate will see this in their dashboard.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 p-4">
          <button
            type="button"
            data-testid="grade-cancel"
            onClick={onClose}
            disabled={saving}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >Cancel</button>
          <button
            type="button"
            data-testid="grade-submit"
            onClick={grade}
            disabled={saving || !status}
            className="inline-flex items-center gap-1 rounded-md bg-[#D4AF37] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-[#0A192F] hover:bg-[#c69f30] disabled:opacity-50"
          >
            {saving ? <Loader2 size={11} className="animate-spin" /> : null}
            Save grade
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusPick({ testid, active, onClick, icon: Icon, label, cls }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`flex flex-col items-center justify-center gap-1 rounded-lg border-2 p-3 text-xs font-bold transition ${
        active ? `${cls} ring-2 ring-offset-1 ring-[#0F4C81]` : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
      }`}
    >
      <Icon size={16} />
      {label}
    </button>
  );
}
